import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_BASE;

const SetAddress = () => {
  const navigate = useNavigate();
  const [autocompleteInput, setAutocompleteInput] = useState("");
  const [unit, setUnit] = useState("");
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [province, setProvince] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [lat, setLat] = useState(null);
  const [lng, setLng] = useState(null);

  const storedEmail = localStorage.getItem("userEmail");

  /* =========================================================
     🗺️ Load Google Maps Autocomplete
  ========================================================= */
  useEffect(() => {
    const loadGoogleMapsScript = () => {
      const existingScript = document.getElementById("googleMaps");
      if (!existingScript) {
        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
        script.id = "googleMaps";
        script.async = true;
        script.defer = true;
        script.onload = initAutocomplete;
        document.body.appendChild(script);
      } else {
        initAutocomplete();
      }
    };

    const initAutocomplete = () => {
      if (!window.google) return;
      const input = document.getElementById("autocompleteAddress");
      const autocomplete = new window.google.maps.places.Autocomplete(input, {
        types: ["geocode"],
      });

      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        if (!place.address_components) return;

        const getComponent = (type) =>
          place.address_components.find((c) => c.types.includes(type))
            ?.long_name || "";

        const fullStreet = `${getComponent("street_number")} ${getComponent(
          "route"
        )}`.trim();

        setAutocompleteInput(place.formatted_address);
        setStreet(fullStreet);
        setCity(getComponent("locality") || getComponent("sublocality"));
        setProvince(getComponent("administrative_area_level_1"));
        setPostalCode(getComponent("postal_code"));
        setLat(place.geometry?.location.lat());
        setLng(place.geometry?.location.lng());
      });
    };

    loadGoogleMapsScript();
  }, []);

  /* =========================================================
     📍 Use My Location
  ========================================================= */
  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation not supported.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        const geocoder = new window.google.maps.Geocoder();

        geocoder.geocode(
          { location: { lat: latitude, lng: longitude } },
          (results, status) => {
            if (status === "OK" && results[0]) {
              const place = results[0];
              setAutocompleteInput(place.formatted_address);

              const getComponent = (type) =>
                place.address_components.find((c) =>
                  c.types.includes(type)
                )?.long_name || "";

              const fullStreet = `${getComponent(
                "street_number"
              )} ${getComponent("route")}`.trim();

              setStreet(fullStreet);
              setCity(getComponent("locality") || getComponent("sublocality"));
              setProvince(
                getComponent("administrative_area_level_1")
              );
              setPostalCode(getComponent("postal_code"));
              setLat(latitude);
              setLng(longitude);
            } else {
              alert("Failed to detect address.");
            }
          }
        );
      },
      () => alert("Unable to retrieve location.")
    );
  };

  /* =========================================================
     💾 Save Address
  ========================================================= */
  const handleSaveAndContinue = async () => {
    if (!street || !city || !province || !postalCode || !lat || !lng) {
      alert("Please complete all required address fields.");
      return;
    }

    const fullAddress = unit
      ? `${unit} - ${street}, ${city}, ${province}, ${postalCode}`
      : `${street}, ${city}, ${province}, ${postalCode}`;

    try {
      await axios.post(`${API_BASE}/api/customers/update-profile`, {
        email: storedEmail,
        address: fullAddress,
        street,
        city,
        province,
        postalCode,
        latitude: lat,
        longitude: lng,
      });

      localStorage.setItem("userAddress", fullAddress);
      localStorage.setItem("userCity", city);
      localStorage.setItem("userProvince", province);
      localStorage.setItem("userPostalCode", postalCode);
      localStorage.setItem("userLat", lat);
      localStorage.setItem("userLng", lng);

      navigate("/upload-photo");
    } catch (error) {
      console.error("❌ Error saving address:", error);
      alert("Failed to save address.");
    }
  };

  /* =========================================================
     🎨 UI (CLEAN & CONSISTENT)
  ========================================================= */
  return (
    <div className="min-h-screen bg-white px-6 py-12">
      <div className="max-w-md mx-auto space-y-6">

        <div>
          <h2 className="text-2xl font-semibold text-gray-800">
            Set your address
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            This helps us find services near you
          </p>
        </div>

        <div className="space-y-4">
          <input
            id="autocompleteAddress"
            type="text"
            placeholder="Start typing your address"
            value={autocompleteInput}
            onChange={(e) => setAutocompleteInput(e.target.value)}
            className="w-full px-4 py-3 rounded-full border border-gray-200 bg-white text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />

          <button
            onClick={handleUseMyLocation}
            className="text-sm text-indigo-600 font-medium hover:underline"
          >
            Use my current location
          </button>

          <input
            type="text"
            placeholder="Unit / Apartment (optional)"
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            className="w-full px-4 py-3 rounded-full border border-gray-200 bg-white text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />

          <input
            type="text"
            placeholder="Street address"
            value={street}
            onChange={(e) => setStreet(e.target.value)}
            className="w-full px-4 py-3 rounded-full border border-gray-200 bg-white text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />

          <div className="flex gap-3">
            <input
              type="text"
              placeholder="City"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="w-full px-4 py-3 rounded-full border border-gray-200 bg-white text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <input
              type="text"
              placeholder="Province"
              value={province}
              onChange={(e) => setProvince(e.target.value)}
              className="w-full px-4 py-3 rounded-full border border-gray-200 bg-white text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <input
            type="text"
            placeholder="Postal code"
            value={postalCode}
            onChange={(e) => setPostalCode(e.target.value)}
            className="w-full px-4 py-3 rounded-full border border-gray-200 bg-white text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />

          <button
            onClick={handleSaveAndContinue}
            className="w-full py-3 rounded-full font-semibold text-white bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 hover:opacity-90 active:scale-95 transition"
          >
            Save & continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default SetAddress;
